import { useState, useEffect } from "react";
import { QrCode, X, ExternalLink, Copy, Check } from "lucide-react";
import { useContent, resolveImage } from "../contexts/ContentContext";

const ACCENT = {
  cashapp: { ring: "ring-[#00D632]", chip: "bg-[#00D632] text-white", text: "text-[#00A126]" },
  zelle:   { ring: "ring-[#6D1ED4]", chip: "bg-[#6D1ED4] text-white", text: "text-[#6D1ED4]" },
  default: { ring: "ring-[#2563EB]", chip: "bg-[#2563EB] text-white", text: "text-[#1D4ED8]" },
};

export const Donate = () => {
  const { data: donate } = useContent("donate");
  const [open, setOpen] = useState(null); // active method index for lightbox
  const [copied, setCopied] = useState(null);

  // Esc to close lightbox + body scroll lock
  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!donate || !(donate.methods || []).length) return null;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* noop */ }
  };

  const methods = donate.methods || [];
  const active = open !== null ? methods[open] : null;
  const accent = active ? (ACCENT[active.accent] || ACCENT.default) : ACCENT.default;

  return (
    <>
      <section
        id="donate"
        data-testid="donate-section"
        className="relative rounded-[28px] overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1D4ED8] text-white p-7 md:p-10 mt-12"
      >
        <div aria-hidden="true" className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FFD100]/20 blur-3xl" />
        <div aria-hidden="true" className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#2563EB]/30 blur-3xl" />

        <div className="relative grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#FFD100]">
              <QrCode className="w-3.5 h-3.5" />
              {donate.eyebrow}
            </span>
            <h3 data-testid="donate-heading" className="mt-3 font-display text-2xl md:text-4xl font-bold leading-tight">
              {donate.heading}
            </h3>
            {donate.intro && (
              <p className="mt-4 text-white/80 text-sm md:text-base leading-relaxed">{donate.intro}</p>
            )}
            {donate.footnote && (
              <p className="mt-6 text-xs text-white/50">{donate.footnote}</p>
            )}
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {methods.map((m, i) => {
              const a = ACCENT[m.accent] || ACCENT.default;
              const qr = resolveImage(m.qr);
              return (
                <div
                  key={i}
                  data-testid={`donate-method-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`bg-white rounded-2xl p-5 md:p-6 ring-2 ${a.ring} shadow-[0_20px_60px_rgba(0,0,0,0.25)] flex flex-col`}
                >
                  <span className={`inline-flex self-start items-center gap-1.5 ${a.chip} text-[10px] font-bold uppercase tracking-[0.16em] px-2.5 py-1 rounded-full`}>
                    {m.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpen(i)}
                    data-testid={`donate-qr-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="mt-4 group relative aspect-square w-full rounded-xl bg-slate-50 grid place-items-center overflow-hidden hover:ring-2 hover:ring-offset-2 hover:ring-[#FFD100] transition-all cursor-zoom-in"
                    aria-label={`Open ${m.name} QR code full size`}
                  >
                    {qr ? (
                      <img src={qr} alt={`${m.name} donation QR code`} className="w-full h-full object-contain p-2" loading="lazy" />
                    ) : (
                      <QrCode className="w-16 h-16 text-slate-300" />
                    )}
                    <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      Tap to enlarge
                    </span>
                  </button>
                  {m.handle && (
                    <button
                      type="button"
                      onClick={() => copy(m.handle, m.name)}
                      data-testid={`donate-handle-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`mt-3 inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 ${a.text} text-xs font-bold transition-colors w-full`}
                    >
                      <span className="truncate text-left">{m.handle}</span>
                      {copied === m.name ? <Check className="w-3.5 h-3.5 shrink-0" /> : <Copy className="w-3.5 h-3.5 shrink-0" />}
                    </button>
                  )}
                  {m.url && (
                    <a
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors"
                    >
                      Open {m.name} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QR Lightbox */}
      {active && (
        <div
          data-testid="donate-lightbox"
          className="fixed inset-0 z-[110] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-fade-in"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${active.name} QR code`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(null); }}
            data-testid="donate-lightbox-close"
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors backdrop-blur"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative bg-white rounded-3xl p-6 md:p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <span className={`inline-flex items-center gap-1.5 ${accent.chip} text-[10px] font-bold uppercase tracking-[0.16em] px-2.5 py-1 rounded-full`}>
              {active.name}
            </span>
            <h4 className="mt-3 font-display text-2xl font-bold text-slate-900">Scan to donate</h4>
            <p className="mt-1 text-sm text-slate-600">Open your camera and point it at this code, or save the image and scan from your phone.</p>
            <div className="mt-5 aspect-square rounded-2xl bg-slate-50 grid place-items-center overflow-hidden">
              <img src={resolveImage(active.qr)} alt={`${active.name} donation QR code`} className="w-full h-full object-contain p-3" />
            </div>
            {active.handle && (
              <p className="mt-4 text-center text-sm font-bold text-slate-900 break-all">{active.handle}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};
